
import { Project } from "@/integrations/supabase/types/";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar, DollarSign } from "lucide-react";
import { format } from "date-fns";

interface ProjectCardProps {
  project: Project;
  onClick: (project: Project) => void;
  viewMode?: "grid" | "list";
}

export const ProjectCard = ({ project, onClick, viewMode = "grid" }: ProjectCardProps) => {
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'planning':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'active':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'completed':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'on-hold':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
    }
  };

  if (viewMode === "list") {
    return (
      <Card 
        className="glass-card-hover p-6 cursor-pointer" 
        onClick={() => onClick(project)}
      >
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h3 className="text-xl font-semibold">{project.name}</h3>
              <Badge className={getStatusColor(project.status)}>
                {project.status}
              </Badge>
            </div>
            <p className="text-muted-foreground text-sm line-clamp-1">
              {project.description}
            </p>
          </div>
          <div className="flex items-center gap-8 text-sm text-muted-foreground">
            <div className="flex items-center">
              <Calendar className="h-4 w-4 mr-1" />
              {project.due_date ? (
                format(new Date(project.due_date), 'MMM d, yyyy')
              ) : (
                'No due date'
              )}
            </div>
            {project.budget && (
              <div className="flex items-center">
                <DollarSign className="h-4 w-4 mr-1" />
                ${project.budget.toLocaleString()}
              </div>
            )}
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card 
      className="hover:shadow-lg transition-shadow cursor-pointer" 
      onClick={() => onClick(project)}
    >
      <CardHeader>
        <div className="flex justify-between items-start">
          <div>
            <CardTitle>{project.name}</CardTitle>
            <CardDescription className="mt-2">{project.description}</CardDescription>
          </div>
          <Badge className={getStatusColor(project.status)}>
            {project.status}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <div className="flex items-center">
            <Calendar className="h-4 w-4 mr-1" />
            {project.due_date ? (
              format(new Date(project.due_date), 'MMM d, yyyy')
            ) : (
              'No due date'
            )}
          </div>
          {project.budget && (
            <div className="flex items-center">
              <DollarSign className="h-4 w-4 mr-1" />
              {project.budget.toLocaleString()}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
